"use client";

import { useEffect, useState } from "react";
import { EventsData } from "../types";
import { categorizeEvents } from "../utils/eventUtils";
import Link from "next/link";
import ScrollAnimation from "./ScrollAnimation";

export default function UpcomingEventsSection() {
  const [data, setData] = useState<EventsData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`/api.php?action=get_events&t=${new Date().getTime()}`)
      .then((res) => res.json())
      .then((jsonData) => {
        setData(jsonData);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Failed to load events", err);
        setLoading(false);
      });
  }, []);

  if (loading || !data) {
    return null; // Don't show anything while loading to avoid flickering
  }

  const { upcoming } = categorizeEvents(data?.events || []);
  // Show all upcoming events in the table as well
  const otherEvents = upcoming;

  if (otherEvents.length === 0) return null;

  return (
    <section className="section-block concert-dates text-center pt-0">
      <ScrollAnimation animation="fade-in">
        <h2 className="mt-0 glitch-hover" data-text="Anstehende Termine">Anstehende Termine</h2>
        <div className="table-container shadow-xl rounded-xl overflow-hidden mt-8">
          <table className="w-full border-collapse bg-black/30 backdrop-blur-sm text-left responsive-table">
            <thead>
              <tr className="bg-primary text-accent uppercase tracking-wider font-oswald">
                <th className="p-5">Datum</th>
                <th className="p-5">Ort</th>
                <th className="p-5">Veranstaltung</th>
                <th className="p-5">Tickets</th>
              </tr>
            </thead>
            <tbody>
              {otherEvents.map((event, index) => (
                <tr key={index} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                  <td className="p-5" data-label="Datum">{event.date}</td>
                  <td className="p-5" data-label="Ort">{event.location}</td>
                  <td className="p-5" data-label="Veranstaltung">{event.event}</td>
                  <td className="p-5" data-label="Tickets">
                    {event.tickets.url && event.tickets.url.trim() !== "" ? (
                      <Link href={event.tickets.url} target="_blank" className="text-accent hover:underline font-bold">
                        {event.tickets.text}
                      </Link>
                    ) : (
                      <span className="opacity-70">{event.tickets.text}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="max-w-4xl mx-auto px-4 mt-16">
          <div className="band-description bg-gradient-to-br from-primary/40 to-black/60 backdrop-blur-md border border-white/10 p-8 md:p-12 rounded-2xl shadow-2xl relative overflow-hidden group">
            {/* Subtle decorative element */}
            <div className="absolute -top-24 -right-24 w-48 h-48 bg-accent/5 rounded-full blur-3xl group-hover:bg-accent/10 transition-colors duration-700" />
            
            <div className="relative z-10 space-y-6 text-lg md:text-xl leading-relaxed font-serif text-gray-200">
              <p>
                Für unsere Live-Auftritte haben wir ein energetisches und mitreißendes Set entwickelt. Spielfreude, Energie und Spaß stehen für uns an oberster Stelle. Unsere Mission ist es, eine Verbindung mit den Zuschauern zu schaffen und für eine gewisse Zeit den Alltag mit ihnen zu vergessen. Mit unseren vielseitigen Clean- und Scream-Vocals, den kraftvollen Gitarren und Dudelsäcken sowie einem brachialen Schlagzeug bringen wir jede Bühne an ihre Grenzen. Unser Set ist wie unsere Musik vielseitig und facettenreich und bietet für jedes Ohr das ganz bestimmte Etwas! 
              </p>
              
              <p className="pt-4 border-t border-white/5">
                Unsere Musik und Live-Performance konnten wir bereits auf zahlreichen Bühnen in ganz NRW und darüber hinaus präsentieren. Ein Überblick über unsere bisherigen Auftritte ist in unserem Gig-Archiv zu finden.
              </p>
            </div>

            {/* Bottom accent line */}
            <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-accent/30 to-transparent" />
          </div>
        </div>
      </ScrollAnimation>
    </section>
  );
}